import React, { useState, useEffect } from "react";
import {
  Building2,
  ShoppingCart,
  Heart,
  Truck,
  ArrowUpRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function WeServe() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const industries = [
    {
      id: "finance",
      title: "Life sciences & Biotech",
      description:
        "We build specialized technology systems and dashboards that streamline laboratory operations, research workflows, and data management.",
      icon: Building2,
      isDark: true,
    },
    {
      id: "retail",
      title: "Construction & Civil Engineering",
      description:
        "We deliver construction technology solutions that provide real-time visibility into projects, resources, and site operations.",
      icon: ShoppingCart,
      isDark: true,
    },
    {
      id: "healthcare",
      title: "Manufacturing & Industry 4.0",
      description:
        "We build smart manufacturing systems that digitize production, improve efficiency, and enable data-driven operations.",
      icon: Heart,
      isDark: true,
    },
    {
      id: "manufacturing",
      title: "Supply Chain & Logistics",
      description:
        "We develop intelligent supply chain systems that provide end-to-end visibility and operational control.",
      icon: Truck,
      isDark: true,
    },
  ];

  return (
    <div className="bg-gradient-to-br from-slate-200 via-slate-100 to-blue-100 py-12 px-6 relative overflow-hidden">
      {/* Decorative network lines */}
      <div className="absolute bottom-0 right-0 w-1/2 h-1/2 opacity-20">
        <svg viewBox="0 0 400 400" className="w-full h-full">
          <line
            x1="50"
            y1="200"
            x2="150"
            y2="100"
            stroke="currentColor"
            strokeWidth="2"
            className="text-slate-400"
          />
          <line
            x1="150"
            y1="100"
            x2="250"
            y2="150"
            stroke="currentColor"
            strokeWidth="2"
            className="text-slate-400"
          />
          <line
            x1="250"
            y1="150"
            x2="350"
            y2="100"
            stroke="currentColor"
            strokeWidth="2"
            className="text-slate-400"
          />
          <line
            x1="50"
            y1="200"
            x2="150"
            y2="300"
            stroke="currentColor"
            strokeWidth="2"
            className="text-slate-400"
          />
          <circle
            cx="50"
            cy="200"
            r="8"
            fill="currentColor"
            className="text-slate-400"
          />
          <circle
            cx="150"
            cy="100"
            r="8"
            fill="currentColor"
            className="text-slate-400"
          />
          <circle
            cx="250"
            cy="150"
            r="8"
            fill="currentColor"
            className="text-slate-400"
          />
          <circle
            cx="350"
            cy="100"
            r="8"
            fill="currentColor"
            className="text-slate-400"
          />
          <circle
            cx="150"
            cy="300"
            r="8"
            fill="currentColor"
            className="text-slate-400"
          />
        </svg>
      </div>

      <div className="container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-start">
          {/* Left Content */}
          <div className="space-y-4 md:space-y-8">
            {/* Badge */}
            <div className="flex justify-center sm:justify-start">
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 border-2 border-slate-900 rounded-full bg-white transition-all duration-1000 ${
                  isVisible
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-8"
                }`}
              >
                <div className="w-2 h-2 bg-slate-900 rounded-sm"></div>
                <span className="text-slate-900 font-semibold text-sm">
                  Industries We Serve
                </span>
              </div>
            </div>

            {/* Heading */}
            <div className="space-y-2 text-center md:text-start">
              <h2
                className={`text-3xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight transition-all duration-1000 delay-200 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
              >
                Tailored IT Solutions
              </h2>
              <h3
                className={`text-3xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight transition-all duration-1000 delay-300 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
              >
                Across <span className="italic">Sectors.</span>
              </h3>
            </div>

            {/* Description */}
            <p
              className={`text-slate-600 text-justify text-base sm:text-lg leading-relaxed max-w-xl transition-all duration-1000 delay-500 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              Quantrovia delivers sector-specific IT capabilities designed to address complex operational demands, enhance system performance, and support long-term business scalability across diverse industries.
            </p>

            {/* Button */}
            <div
              className={`pt-4 transition-all duration-1000 delay-700 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              <button 
              onClick={()=>{
                navigate("/industries");
                window.scrollTo({
                  top:0,
                  behavior: "smooth"
                })
              }}
              className="group px-8 py-4 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center gap-3">
                View All Industries
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Right Content - Industry Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              const delay = 800 + index * 150;
              const isHovered = hoveredCard === industry.id;

              return (
                <div
                  key={industry.id}
                  className={`transition-all duration-1000 ${
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-12"
                  }`}
                  style={{ transitionDelay: `${delay}ms` }}
                  onMouseEnter={() => setHoveredCard(industry.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div
                    className={`relative rounded-3xl bg-slate-900 p-8 h-full transition-all duration-500 cursor-pointer overflow-hidden group
                    ${isHovered ? " text-gray-200" : " text-gray-50 border-2 border-slate-200"}
                  `}
                  >
                    {/* Hover overlay (optional smooth effect) */}
                    <div
                      className={`absolute inset-0  transition-all duration-500 ${
                        isHovered ? "opacity-100 " : "opacity-0 bg-"
                      }`}
                    ></div>

                    {/* Content */}
                    <div className="relative z-10">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-6">
                        <h3 className="text-sm font-bold tracking-wide uppercase transition-colors duration-500 text-white group-hover:text-blue-500">
                          {industry.title}
                        </h3>

                        <div
                          className={`flex-shrink-0 w-12 h-12 bg-orange-300 rounded-xl flex items-center justify-center transition-all duration-500
         
          `}
                        >
                          <Icon
                            className={`w-6 h-6 transition-colors duration-500 text-orange-900 
                              `}
                          />
                        </div>
                      </div>

                      {/* Decorative sparkle */}
                      <div
                        className={`mb-6 transition-opacity duration-500 ${
                          isHovered ? "opacity-100" : "opacity-100"
                        }`}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          className="text-purple-400"
                        >
                          <path
                            d="M10 0l2 7l7 2l-7 2l-2 7l-2-7l-7-2l7-2z"
                            fill="currentColor"
                            opacity="0.5"
                          />
                        </svg>
                      </div>

                      {/* Description */}
                      <p
                        className={`text-sm leading-relaxed transition-colors duration-500 text-slate-300
                         `}
                      >
                        {industry.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
